import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useClientAuth } from "@/lib/clientAuth";
import { ArrowUpRight, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "aximo_client_token";

export default function ClientResetPassword() {
    const { client, loading } = useClientAuth();
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get("token") || "";
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);

    if (loading)
        return (
            <div className="min-h-screen bg-ink flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-ember" />
            </div>
        );
    if (client) return <Navigate to="/portal" replace />;

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!token) {
            toast.error("Invalid reset link");
            return;
        }
        if (password.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }
        if (password !== confirm) {
            toast.error("Passwords don't match");
            return;
        }
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/clients/reset-password`, {
                token,
                password,
            });
            localStorage.setItem(TOKEN_KEY, data.access_token);
            toast.success("Password updated");
            // Force a full reload so ClientAuthProvider picks up the new token
            window.location.replace("/portal");
        } catch (err) {
            const msg = err?.response?.data?.detail || "Reset failed";
            toast.error(typeof msg === "string" ? msg : "Reset failed");
        } finally {
            setBusy(false);
        }
    };

    if (!token) {
        return (
            <main
                data-testid="reset-invalid"
                className="min-h-screen bg-ink text-bone flex items-center justify-center px-6 grain"
            >
                <div className="max-w-md text-center">
                    <h1 className="font-display text-3xl tracking-tightest mb-4">
                        Invalid reset link
                    </h1>
                    <p className="text-sm text-bone-500 mb-6">
                        This link is missing a token. Request a new one below.
                    </p>
                    <button
                        onClick={() => navigate("/portal/forgot-password")}
                        className="btn-ember h-11 px-5 text-sm"
                    >
                        Request new link
                    </button>
                </div>
            </main>
        );
    }

    return (
        <main
            data-testid="reset-password-page"
            className="min-h-screen bg-ink text-bone flex items-center justify-center px-6 grain"
        >
            <div className="w-full max-w-md">
                <Link to="/" className="flex items-center gap-3 mb-12">
                    <img src="/logo.png" alt="Aximo Studios" className="h-12 w-auto" />
                </Link>
                <div className="flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-ember mb-6">
                    <ShieldCheck className="w-3.5 h-3.5" />
                    Set a new password
                </div>
                <h1 className="font-display text-bone text-5xl tracking-tightest leading-[1] mb-10">
                    New
                    <br />
                    <span className="font-serif-i italic text-ember">password.</span>
                </h1>
                <form
                    onSubmit={onSubmit}
                    className="space-y-6 border border-white/10 bg-ink-800/40 p-8"
                    data-testid="reset-form"
                >
                    <Field
                        label="New password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        testid="reset-password"
                        placeholder="Min. 8 characters"
                    />
                    <Field
                        label="Confirm password"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        testid="reset-confirm"
                        placeholder="Repeat password"
                    />
                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="reset-submit"
                        className="btn-ember w-full inline-flex items-center justify-between h-12 px-5 text-sm font-medium tracking-wide disabled:opacity-60"
                    >
                        {busy ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <span>Update password</span>
                        )}
                        <ArrowUpRight className="w-4 h-4" />
                    </button>
                </form>
            </div>
        </main>
    );
}

function Field({ label, value, onChange, testid, placeholder }) {
    return (
        <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-2">
                {label}
            </span>
            <input
                type="password"
                value={value}
                onChange={onChange}
                data-testid={testid}
                placeholder={placeholder}
                className="w-full bg-transparent border-0 border-b border-white/15 focus:border-ember outline-none py-3 text-bone placeholder:text-bone-500 font-display text-lg tracking-tight transition-colors"
            />
        </label>
    );
}
